import * as Sentry from '@sentry/vue'

interface ImageSentryContext {
  imageId: string
  imageType: string
  topicId: string
  provider?: string
  externalId?: string
}

export const useImageSentry = (context: ImageSentryContext) => {
  const captureImageError = (
    error: Error,
    actionContext: string,
    additionalData?: Record<string, unknown>,
    componentName: string = 'ImageEdit'
  ) => {
    Sentry.addBreadcrumb({
      category: 'image-edit',
      message: `Image edit error in ${actionContext}`,
      data: {
        imageId: context.imageId,
        imageType: context.imageType,
        topicId: context.topicId,
        provider: context.provider,
        externalId: context.externalId,
        ...additionalData,
      },
      level: 'error',
    })

    Sentry.captureException(error, {
      level: 'error',
      tags: {
        component: componentName,
        action: actionContext,
        imageType: context.imageType,
      },
      extra: {
        imageId: context.imageId,
        topicId: context.topicId,
        provider: context.provider,
        externalId: context.externalId,
        ...additionalData,
      },
    })
  }

  const captureImageWarning = (
    message: string,
    additionalData?: Record<string, unknown>,
    componentName: string = 'ImageEdit'
  ) => {
    Sentry.addBreadcrumb({
      category: 'image-edit',
      message,
      data: {
        imageId: context.imageId,
        imageType: context.imageType,
        topicId: context.topicId,
        ...additionalData,
      },
      level: 'warning',
    })

    Sentry.captureMessage(message, {
      level: 'warning',
      tags: {
        component: componentName,
        imageType: context.imageType,
      },
      extra: {
        imageId: context.imageId,
        topicId: context.topicId,
        ...additionalData,
      },
    })
  }

  const addBreadcrumb = (
    message: string,
    data?: Record<string, unknown>,
    level: 'info' | 'warning' | 'error' = 'info'
  ) => {
    Sentry.addBreadcrumb({
      category: 'image-edit',
      message,
      data: {
        imageId: context.imageId,
        imageType: context.imageType,
        topicId: context.topicId,
        ...(data || {}),
      },
      level,
    })
  }

  return {
    captureImageError,
    captureImageWarning,
    addBreadcrumb,
  }
}
